import React, { useEffect, useState } from "react";
import { X, LinkSimple, PaperPlaneTilt, Sparkle, DownloadSimple, FacebookLogo, TwitterLogo, WhatsappLogo } from "@phosphor-icons/react";
import { api, avatarUrl } from "@/lib/api";
import logger from "@/lib/logger";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Bottom-sheet share dialog — Facebook-style.
 * Props:
 *   open        boolean
 *   onClose     fn
 *   postId      string  (post being shared)
 *   postUrl     string  (canonical preview link)
 *   authorName  string  (for share text)
 *   mediaUrl    string? (full URL of the media to "Save" — image or video)
 *   mediaKind   "image" | "video" | null
 */
export default function ShareSheet({ open, onClose, postId, postUrl, authorName = "someone", mediaUrl, mediaKind }) {
  const navigate = useNavigate();
  const [friends, setFriends] = useState(null);
  const [sending, setSending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/dms/conversations");
        setFriends((data || []).slice(0, 12));
      } catch (err) {
        logger.warn("share-sheet", "load conversations failed", err);
        setFriends([]);
      }
    })();
  }, [open]);

  if (!open) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({
        title: "Voxly",
        text: `Check out ${authorName}'s voice post on Voxly`,
        url: postUrl,
      });
    } catch (err) { logger.warn("share-sheet", "native share dismissed or failed", err); }
  };

  const sendToFriend = async (conv) => {
    setSending(conv.conv_id);
    try {
      await api.post(`/dms/conversations/${conv.conv_id}/messages`, {
        text: `Check this out 👀\n${postUrl}`,
      });
      toast.success(`Sent to ${conv.other_name || conv.other?.name || "friend"}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Slow down" : "Couldn't send");
    } finally { setSending(null); }
  };

  const shareToStory = () => {
    onClose();
    navigate(`/stories/create?from=${encodeURIComponent(postId)}`);
  };

  // Download the media (image or video) to the user's device. On phones this
  // saves the file to Downloads (or prompts to add to the Photos library on
  // iOS via Web Share level 2). On desktop it triggers a normal browser
  // download. Falls back to opening the file in a new tab if blob fetch fails.
  const saveToDevice = async () => {
    if (!mediaUrl) { toast.error("Nothing to save on this post."); return; }
    setSaving(true);
    try {
      const resp = await fetch(mediaUrl, { credentials: "include" });
      const blob = await resp.blob();
      const ext = (mediaKind === "video" ? "mp4" : (blob.type.split("/")[1] || "jpg")).split("+")[0];
      const filename = `voxly-${(postId || "post").slice(0, 12)}.${ext}`;

      // Try Web Share Level 2 (lets iOS save into Photos via the share sheet).
      if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: blob.type })] })) {
        try {
          await navigator.share({ files: [new File([blob], filename, { type: blob.type })], title: "Voxly" });
          toast.success("Saved");
          return;
        } catch (err) {
          logger.warn("share-sheet", "navigator.share(files) dismissed/failed; falling back to direct download", err);
        }
      }

      // Standard download fallback (Chromium / desktop / most Android).
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success("Saved to your device");
    } catch (err) {
      logger.warn("share-sheet", "save failed", err);
      // Last-ditch: open in a new tab so the user can long-press save themselves.
      window.open(mediaUrl, "_blank", "noopener,noreferrer");
      toast.info("Opened the media — long-press to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-card border-t border-white/10 sm:border sm:rounded-3xl rounded-t-3xl p-5 pb-8 shadow-2xl animate-in slide-in-from-bottom-8 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold tracking-tight">Share</h3>
          <button onClick={onClose} aria-label="Close" data-testid="share-sheet-close" className="p-1.5 rounded-full hover:bg-white/5 text-muted-foreground">
            <X size={20} />
          </button>
        </div>

        {/* Quick actions row */}
        <div className="grid grid-cols-4 gap-3 pb-5 border-b border-white/5">
          <QuickAction icon={LinkSimple} label="Copy link" onClick={copyLink} testid="share-copy-link" />
          <QuickAction icon={Sparkle} label="To Story" onClick={shareToStory} testid="share-to-story" accent />
          <QuickAction icon={PaperPlaneTilt} label="More" onClick={nativeShare} testid="share-native" />
          <QuickAction
            icon={DownloadSimple}
            label={saving ? "Saving…" : "Save"}
            onClick={mediaUrl ? saveToDevice : () => toast.info("This post has nothing to save.")}
            testid="share-save"
            disabled={saving || !mediaUrl}
          />
        </div>

        {/* External quick shares (open intent URLs) */}
        <div className="flex items-center gap-3 py-4 overflow-x-auto">
          <ExternalLink icon={WhatsappLogo} label="WhatsApp" color="text-emerald-500" href={`https://wa.me/?text=${encodeURIComponent(postUrl)}`} testid="share-whatsapp" />
          <ExternalLink icon={FacebookLogo} label="Facebook" color="text-sky-500" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`} testid="share-facebook" />
          <ExternalLink icon={TwitterLogo} label="X" color="text-foreground" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}`} testid="share-twitter" />
        </div>

        {/* Send-to-friend list */}
        <div className="pt-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70 mb-2">Send in a chat</p>
          {friends === null ? (
            <div className="py-6 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary/60 border-t-transparent animate-spin" /></div>
          ) : friends.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4">No chats yet. Start a conversation from Discover.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto -mx-1 px-1 space-y-1">
              {friends.map((c) => {
                const name = c.other_name || c.other?.name || "Friend";
                const pic = c.other_picture || c.other?.picture;
                return (
                  <button
                    key={c.conv_id}
                    onClick={() => sendToFriend(c)}
                    disabled={sending === c.conv_id}
                    data-testid={`share-to-${c.conv_id}`}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-2xl hover:bg-white/5 transition disabled:opacity-50"
                  >
                    <img src={avatarUrl(pic)} alt="" className="h-9 w-9 rounded-full object-cover" />
                    <span className="flex-1 text-left text-sm font-semibold truncate">{name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-primary">
                      {sending === c.conv_id ? "…" : "SEND"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid, accent, disabled }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      disabled={disabled}
      className="flex flex-col items-center gap-1.5 group disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className={`h-12 w-12 rounded-full flex items-center justify-center ${accent ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"} group-hover:scale-105 transition`}>
        <Icon size={22} weight={accent ? "fill" : "bold"} />
      </span>
      <span className="text-[11px] text-muted-foreground group-hover:text-foreground">{label}</span>
    </button>
  );
}

function ExternalLink({ icon: Icon, label, color, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 group shrink-0"
    >
      <span className={`h-11 w-11 rounded-full bg-secondary flex items-center justify-center ${color} group-hover:scale-105 transition`}>
        <Icon size={22} weight="fill" />
      </span>
      <span className="text-[10px] text-muted-foreground group-hover:text-foreground">{label}</span>
    </a>
  );
}
